import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Video, User, LogOut, LayoutDashboard, Shield, Building2, Link2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Track scroll position for glassmorphism effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Check if user is admin
  const isAdmin = user?.is_admin === true;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-zinc-950/80 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20' 
          : 'bg-gradient-to-b from-zinc-950/90 via-zinc-950/50 to-transparent'
      }`} 
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Image Version */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img data-testid="brand-logo"
              src={LOGO_URL} 
              alt="BrandStage" 
              className={`transition-all duration-300 ${scrolled ? 'h-10 md:h-12' : 'h-12 md:h-14'} w-auto`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <a href="/#services" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-services">Services</a>
            <Link to="/about" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-about">About</Link>
            <Link to="/message-from-founder" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-founder">Founder's Message</Link>
            <a href="/#process" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-process">How It Works</a>
            <a href="/#pricing" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-pricing">Pricing</a>
            <Link to="/booth" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-booth">Booth</Link>
            <Link to="/connect" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium flex items-center gap-1" data-testid="nav-connect">
              <Link2 className="w-4 h-4" />
              Connect
            </Link>
            <a href="/#contact" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-contact">Contact</a>
            {isAdmin && (
              <Link to="/admin" className="text-[#C41E3A] hover:text-[#E85A6F] transition-colors text-sm font-bold flex items-center gap-1" data-testid="nav-admin">
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <div className="relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 text-zinc-300 hover:text-white transition-colors"
                  data-testid="user-menu-button"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isAdmin ? 'bg-[#C41E3A]' : 'bg-zinc-700'}`}>
                    {isAdmin ? <Shield className="w-4 h-4 text-white" /> : <User className="w-4 h-4 text-zinc-300" />}
                  </div>
                  <span className="text-sm font-medium">{user.name}</span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-zinc-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-xl" data-testid="user-dropdown">
                    {isAdmin && (
                      <Link 
                        to="/admin" 
                        className="flex items-center gap-2 px-4 py-3 text-sm text-[#C41E3A] hover:text-white hover:bg-white/10 transition-colors font-bold rounded-t-xl"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="admin-link"
                      >
                        <Shield className="w-4 h-4" />
                        Admin Dashboard
                      </Link>
                    )}
                    <Link 
                      to="/dashboard" 
                      className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                      data-testid="dashboard-link"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                    <Link 
                      to="/studio" 
                      className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                      data-testid="studio-link"
                    >
                      <Video className="w-4 h-4" />
                      Recording Studio
                    </Link>
                    <Link 
                      to="/profile/create" 
                      className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                      data-testid="profile-create-link"
                    >
                      <Building2 className="w-4 h-4" />
                      Create Profile
                    </Link>
                    <button 
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-3 text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors border-t border-white/10 rounded-b-xl"
                      data-testid="logout-button"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="text-zinc-300 hover:text-white transition-colors text-sm font-medium"
                  data-testid="login-link"
                >
                  Log In
                </Link>
                <Link 
                  to="/booth" 
                  className="bg-gradient-to-r from-[#C41E3A] to-[#9B1830] hover:from-[#D42E4A] hover:to-[#B01840] text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-lg shadow-[#C41E3A]/20 hover:shadow-[#C41E3A]/40"
                  data-testid="record-cta"
                >
                  Record Quick Bite
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-zinc-300 hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu with Glassmorphism */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4 bg-zinc-950/95 backdrop-blur-xl rounded-xl" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              <a href="/#services" className="text-zinc-300 hover:text-white transition-colors">Services</a>
              <Link to="/about" className="text-zinc-300 hover:text-white transition-colors">About</Link>
              <Link to="/message-from-founder" className="text-zinc-300 hover:text-white transition-colors" data-testid="mobile-nav-founder">Founder's Message</Link>
              <a href="/#process" className="text-zinc-300 hover:text-white transition-colors">How It Works</a>
              <a href="/#pricing" className="text-zinc-300 hover:text-white transition-colors">Pricing</a>
              <Link to="/booth" className="text-zinc-300 hover:text-white transition-colors">Booth</Link>
              <Link to="/connect" className="text-zinc-300 hover:text-white transition-colors flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Connect
              </Link>
              <a href="/#contact" className="text-zinc-300 hover:text-white transition-colors">Contact</a>
              {user ? (
                <>
                  {isAdmin && (
                    <Link to="/admin" className="text-[#C41E3A] hover:text-[#E85A6F] font-bold flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Admin Dashboard
                    </Link>
                  )}
                  <Link to="/dashboard" className="text-zinc-300 hover:text-white transition-colors">Dashboard</Link>
                  <Link to="/studio" className="text-zinc-300 hover:text-white transition-colors">Recording Studio</Link>
                  <Link to="/profile/create" className="text-zinc-300 hover:text-white transition-colors">Create Profile</Link>
                  <button onClick={handleLogout} className="text-left text-zinc-300 hover:text-white transition-colors">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-zinc-300 hover:text-white transition-colors">Log In</Link>
                  <Link to="/studio" className="bg-gradient-to-r from-[#C41E3A] to-[#9B1830] text-white px-4 py-2 rounded-xl text-center font-semibold">Record Now</Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
